import React, { useEffect, useState } from "react";

import { updateUserVerificationFarmTask, updateUserVerificationFarmTaskAdmin } from "services";

import { handleFileUpload, hashFile, useCancelAsyncOnUnmount, useTranslation, useUserContext } from "utils";

import { COUNTRY_CODE, VERIFICATION_ALLOWED_FILE_TYPES, VERIFICATION_DOCUMENTS_PDF_URL } from "consts";

import { Button, Checkbox, Col, Drawer, ExtraInfo, Form, LinkMoreInfo, Row, TextArea, Trans, Typography, UploadFile, Message as message } from "components";

import { ContactSupport } from "pages/app/farm/components/ContactSupport";

const defaultDocument = {
  success: false,
  uploading: false,
  link: null,
  filename: null,
  fileHash: null,
  errorMessage: null,
};

export const SubmitTaskDrawer = ({ farmTask, visible, onClose, onFinish, isAdmin }) => {
  const { t } = useTranslation("tasks");
  const { user } = useUserContext();

  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState([{ ...defaultDocument }]);
  const [showInfoDrawer, setShowInfoDrawer] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form] = Form.useForm();
  const hasNoDocument = Form.useWatch("hasNoDocument", form);

  const signal = useCancelAsyncOnUnmount()();

  useEffect(() => {
    if (!farmTask?.documents?.length) {
      return;
    }

    // Once farmTask is set we want to inject already existing docs
    setDocuments((docs) => {
      // Just a safeguard to check if already injected
      if (docs.length > 1) {
        return docs;
      }

      return [
        ...farmTask.documents.map((doc) => ({
          link: doc.link,
          filename: doc.filename,
          fileHash: doc.fileHash,
          uploading: false,
          success: true,
          errorMessage: null,
          fromDocScan: doc.fromDocScan,
        })),
        ...docs,
      ];
    });
  }, [farmTask]);

  const handleUpload = (file, i) => {
    const isValidType = VERIFICATION_ALLOWED_FILE_TYPES.some((type) => type === file.type);

    const documentsCopy = [...documents];

    if (!documentsCopy[i]) {
      return;
    }

    if (!isValidType) {
      documentsCopy[i].errorMessage = t("submitTaskDrawer.upload.invalidType");
      setDocuments(documentsCopy);
      return;
    }

    documentsCopy.push({ ...defaultDocument });

    documentsCopy[i].uploading = true;
    setDocuments(documentsCopy);

    setUploading(true);

    Promise.all([
      handleFileUpload({
        file,
        location: "verifications",
      }),
      hashFile(file),
    ])
      .then(([res, fileHash]) => {
        // Use previous documents here to avoid issues of skipped rerenders
        setDocuments((prev) => {
          const prevCopy = [...prev];

          prevCopy[i] = {
            link: res.location,
            uploading: false,
            success: true,
            filename: file.name.split(".").slice(0, -1).join("."),
            fileHash,
            errorMessage: null,
          };

          return prevCopy;
        });
      })
      .finally(() => setUploading(false))
      .catch((_err) => {
        // Use previous documents here to avoid issues of skipped rerenders
        setDocuments((prev) => {
          const prevCopy = [...prev];

          prevCopy[i].uploading = false;
          prevCopy[i].errorMessage = t("submitTaskDrawer.upload.genericErrorMessage");

          return documentsCopy;
        });
        setUploading(false);
      });
  };

  const handleDelete = (e, i) => {
    e.preventDefault();
    e.stopPropagation();

    const documentsCopy = [...documents];
    documentsCopy.splice(i, 1);
    setDocuments(documentsCopy);
  };

  const handleClose = () => {
    // Reset form and documents
    form.resetFields();
    setDocuments([{ ...defaultDocument }]);

    onClose?.();
  };

  const complete = ({ hasNoDocument, note }) => {
    // First check if there are documents uploaded if the checkbox "hasNoDocument" is not ticked
    if (!hasNoDocument && documents.length === 1 && !documents[0].success) {
      const documentsCopy = [...documents];
      documentsCopy[0].errorMessage = t("submitTaskDrawer.upload.mandatoryErrorMessage");
      setDocuments(documentsCopy);
      return;
    }

    setSaving(true);
    const updateTask = !isAdmin ? updateUserVerificationFarmTask : updateUserVerificationFarmTaskAdmin;

    updateTask({
      id: farmTask.id,
      documents: hasNoDocument ? [] : documents.filter(({ success }) => success).map(({ link, filename, fileHash }) => ({ link, filename, fileHash })),
      uploaded: !hasNoDocument,
      note,
      signal,
    })
      .then(() => {
        message({ content: t("submitTaskDrawer.successMessage"), icon: "tick" });
        onFinish?.();
        handleClose();
        onClose?.();
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Drawer
      dataTestId="submit-task-drawer"
      title={t("submitTaskDrawer.title")}
      visible={visible}
      onClose={handleClose}
      footer={
        <Button block loading={saving || uploading} type="primary" onClick={() => form.submit()}>
          {t("submitTaskDrawer.submit")}
        </Button>
      }
    >
      <Row>
        <Col>
          <Typography variant="h1">{t("submitTaskDrawer.headline")}</Typography>
          <Typography variant="paragraph-m">
            {/* Use Trans to use b tag in translation without using dangerouslySetInnerHtml */}
            <Trans i18nKey="tasks:submitTaskDrawer.description.upload">
              <b>{{ name: farmTask?.task?.documentName }}</b>
              <b>{{ year: farmTask?.year }}</b>
            </Trans>
            {!hasNoDocument && ` ${t("submitTaskDrawer.description.ourTeam")}`}
          </Typography>
          <LinkMoreInfo onClick={() => setShowInfoDrawer(true)}>{t("submitTaskDrawer.infoLink")}</LinkMoreInfo>
          {farmTask?.task?.description && <Typography variant="paragraph-m">{farmTask?.task?.description}</Typography>}
        </Col>
      </Row>

      {user?.farm?.country === COUNTRY_CODE.GERMANY && (
        <Row>
          <Col>
            <LinkMoreInfo onClick={() => window.open(VERIFICATION_DOCUMENTS_PDF_URL, "_blank", "noopener,noreferrer")}>{t("documentLink")}</LinkMoreInfo>
          </Col>
        </Row>
      )}

      <div className="margin-top-250">
        {farmTask?.completedAt ? (
          <Typography variant="paragraph-m">{t("submitTaskDrawer.alreadyCompleted")}</Typography>
        ) : (
          <Form form={form} name="task" onFinish={complete}>
            <Row>
              <Col>
                <Form.Item initialValue={false} name="hasNoDocument" valuePropName="checked">
                  <Checkbox>{t("submitTaskDrawer.noDocument")}</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {!hasNoDocument &&
              documents.map(({ fromDocScan, fileHash: _fileHash, ...document }, i) => (
                <Row key={i}>
                  <Col>
                    <UploadFile
                      helpMessage={t("submitTaskDrawer.upload.helpMessage")}
                      label={t("submitTaskDrawer.upload.label")}
                      {...document}
                      beforeUpload={(file) => handleUpload(file, i)}
                      onDelete={(e) => handleDelete(e, i)}
                    />
                    {fromDocScan && (
                      <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
                        {t("submitTaskDrawer.upload.fromDocScanNote")}
                      </Typography>
                    )}
                  </Col>
                </Row>
              ))}

            <Row>
              <Col>
                <Typography variant="paragraph-m" className="margin-top-100 margin-bottom-0">
                  {hasNoDocument ? t("submitTaskDrawer.note.descriptionNoDocument") : t("submitTaskDrawer.note.description")}
                </Typography>
              </Col>
            </Row>

            <Row>
              <Col>
                <TextArea name="note" label={t("submitTaskDrawer.note.label")} autoSize={{ minRows: 4 }} rules={[{ required: hasNoDocument }]} />
              </Col>
            </Row>
          </Form>
        )}
      </div>
      <Row className="margin-top-200 margin-bottom-200">
        <Col>
          <ContactSupport />
        </Col>
      </Row>
      <ExtraInfo setVisible={setShowInfoDrawer} type="verification-tasks" visible={showInfoDrawer} />
    </Drawer>
  );
};

export default SubmitTaskDrawer;
